/**
 * 深度比较两个值是否完全相等
 * @param a 要比较的第一个值
 * @param b 要比较的第二个值
 * @returns 如果两个值深度相等则返回true，否则返回false
 */
export function deepEqual<T>(a: T, b: T): boolean {
  // 引用相同直接返回true
  if (a === b) {
    return true;
  }

  // 检查基本类型
  if (typeof a !== typeof b) {
    return false;
  }

  // 处理null情况（typeof null会返回'object'，需要特殊处理）
  if (a === null || b === null) {
    return a === b;
  }

  // 处理日期类型
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // 处理正则表达式
  if (a instanceof RegExp && b instanceof RegExp) {
    return (
      a.source === b.source &&
      a.flags === b.flags &&
      a.lastIndex === b.lastIndex
    );
  }

  // 处理数组
  if (Array.isArray(a) && Array.isArray(b)) {
    // 数组长度不同直接返回false
    if (a.length !== b.length) {
      return false;
    }
    // 递归比较数组中的每个元素
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  // 处理对象（非数组，非基本类型）
  if (typeof a === 'object' && typeof b === 'object') {
    // 获取对象的键
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    // 键的数量不同直接返回false
    if (keysA.length !== keysB.length) {
      return false;
    }

    // 检查是否有循环引用的情况
    const circularCheck = new WeakSet();

    function checkCircularAndCompare(obj1: object, obj2: object): boolean {
      // 如果已经检查过这对对象，避免循环引用导致的无限递归
      if (circularCheck.has(obj1) && circularCheck.has(obj2)) {
        return true;
      }

      // 将当前对象对加入检查集合
      circularCheck.add(obj1);
      circularCheck.add(obj2);

      // 递归比较每个属性
      for (const key of keysA) {
        // 检查键是否存在
        if (!keysB.includes(key)) {
          return false;
        }

        // 递归比较属性值
        if (!deepEqual(obj1[key as keyof object], obj2[key as keyof object])) {
          return false;
        }
      }

      // 移除检查标记
      circularCheck.delete(obj1);
      circularCheck.delete(obj2);

      return true;
    }

    return checkCircularAndCompare(a, b);
  }

  // 所有检查都不通过，返回false
  return false;
}

export default deepEqual;
